import {
  MouseEvent,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  Directions,
  FieldControlApi,
  FormField,
  PayLoadValue,
} from '@cashier/cashier-model';
import {
  AmountConverter,
  eventBus,
  EventFactory,
  useDrag,
} from '@cashier/cashier-shared';
import { cn } from '@cashier/cashier-ui';

import {
  useAmountValueContext,
  useFieldRegistryContext,
} from '../../../contexts';
import { useFieldToucher, useFormPayloadItem } from '../../../hooks';
import { BonusBadge } from '../../bonus';

import styles from './styles.module.scss';

interface AmountSelectorProps {
  field: FormField;
  direction?: Directions;
  version: 'v2' | 'v3';
}

export const AmountSelector = ({
  field,
  direction,
  version,
}: AmountSelectorProps): ReactElement | null => {
  const { options, currency } = field.params || {};
  const [value, setValue] = useState<string>(
    field.value ? (field.value as string) : '',
  );

  const scrollRef = useRef<HTMLDivElement>(null);

  const apiRef = useRef<FieldControlApi<PayLoadValue> | null>(null);

  const { dragStart, dragStop, dragMove } = useDrag();

  const { setValue: setContextValue } = useAmountValueContext();

  const registry = useFieldRegistryContext();
  const { touch } = useFieldToucher();

  const { isDisabled } = useFormPayloadItem(field.name);

  apiRef.current ??= {
    getValue: () => value,
    setValue: (value) => {
      setValue(String(value ?? ''));
    },
  };

  useEffect(() => {
    if (registry && apiRef.current) {
      return registry.registerControl(field.name, apiRef.current);
    }
  }, [field.name, registry]);

  const handleChange = useCallback(
    (value: string) => {
      if (!isDisabled) {
        touch({ ...field, value }, { interactionCompleted: true });

        setValue(value);
        setContextValue(value);

        eventBus.emit(EventFactory.onAmountChosen(value));
      }
    },
    [field, touch, isDisabled, setContextValue],
  );

  const handleDragMove = (e: MouseEvent) => {
    dragMove(e, (diff) => {
      if (scrollRef.current) {
        scrollRef.current.scrollLeft += diff;
      }
    });
  };

  const bonusBadgeVariant = version === 'v2' ? 'inline' : 'floating';

  return (
    <div
      ref={version === 'v3' ? scrollRef : undefined}
      className={cn({
        [styles.containerV2]: version === 'v2',
        [styles.containerV3]: version === 'v3',
      })}
      data-test="favorite-amounts-container"
      onMouseDown={version === 'v3' ? dragStart : undefined}
      onMouseUp={version === 'v3' ? dragStop : undefined}
      onMouseLeave={version === 'v3' ? dragStop : undefined}
      onMouseMove={version === 'v3' ? handleDragMove : undefined}
    >
      {options?.map((item, key) => (
        <div
          data-test="favorite-amount"
          key={`favorite-amount-item_${key}`}
          className={cn({
            [styles.suggestedItem]: version === 'v2',
            [styles.suggestedItemV3]: version === 'v3',
            [styles.active]: value === item.value,
            [styles.rtl]: direction === Directions.Rtl,
          })}
          onClick={() => handleChange(item.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              handleChange(item.value);
            }
          }}
        >
          <span>
            {version === 'v3' && currency}{' '}
            {AmountConverter.toDelimitedNumber(item.value)}{' '}
            {version === 'v2' && currency}
          </span>
          <BonusBadge amount={item.value} variant={bonusBadgeVariant} />
        </div>
      ))}
    </div>
  );
};
